import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styles from "./InvoiceNumberingSettings.module.css";

const InvoiceNumberingSettings = ({
  show,
  onHide,
  onSave,
  currentCounter,
  validateInvoiceNumber
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // Load saved settings from localStorage with Android-style keys
  const savedMode = localStorage.getItem("invoiceNumberGenerationType");
  const [numberingType, setNumberingType] = useState(
    savedMode === "manual_each_time" ? "manual_each_time" :
    savedMode === "manual_once" ? "manual_once" : "auto"
  );

  const [prefix, setPrefix] = useState(
    localStorage.getItem("invoicePrefix") || "INV-"
  );
  const [includeYear, setIncludeYear] = useState(
    localStorage.getItem("invoiceIncludeYear") === "true"
  );
  const [counter, setCounter] = useState(currentCounter || 1);
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState("");

  // Update counter when currentCounter prop changes
  useEffect(() => {
    if (currentCounter) {
      setCounter(currentCounter);
    }
  }, [currentCounter]);

  // Generate preview
  const generatePreview = useCallback(() => {
    const year = includeYear ? new Date().getFullYear() : "";
    const formattedCounter = String(counter).padStart(5, "0");
    const yearPart = year ? `${year}-` : "";
    // Always use LTR format: prefix + year (if included) + counter
    return `${prefix}${yearPart}${formattedCounter}`;
  }, [prefix, includeYear, counter]);

  const [preview, setPreview] = useState("");

  // Update preview when settings change
  useEffect(() => {
    setPreview(generatePreview());
  }, [generatePreview, numberingType]);

  // Handle save
  const handleSave = async () => {
    // Map numbering type to mode for backward compatibility
    const mode = (numberingType === "manual_each_time" || numberingType === "manual_once") ? "manual" : "auto";

    // Save settings to localStorage with Android-style keys
    localStorage.setItem("invoiceNumberGenerationType", numberingType);
    localStorage.setItem("invoiceNumberingMode", mode); // Keep for backward compatibility
    localStorage.setItem("invoicePrefix", prefix);
    localStorage.setItem("invoiceIncludeYear", includeYear.toString());

    const isManualMode = numberingType === "manual_each_time" || numberingType === "manual_once";

    if (!isManualMode) {
      // Validate the generated number
      setValidating(true);
      setValidationError("");

      try {
        const isValid = await validateInvoiceNumber(preview);
        if (!isValid) {
          setValidationError(t("This invoice number already exists"));
          setValidating(false);
          return;
        }
      } catch (error) {
        console.error("Validation error:", error);
      }
      setValidating(false);
    }

    // Pass settings back to parent - match Android callback signature
    onSave({
      mode,
      prefix,
      includeYear,
      counter,
      invoiceNumber: !isManualMode ? preview : null,
      isManual: isManualMode
    });

    onHide();
  };

  const handleNumberingTypeChange = (e) => {
    const newType = e.target.value;
    setNumberingType(newType);
    setValidationError("");

    // Reset include year for manual modes
    if (newType === "manual_each_time" || newType === "manual_once") {
      setIncludeYear(false);
    }
  };

  const handlePrefixChange = (e) => {
    const newPrefix = e.target.value;
    setPrefix(newPrefix);
    setValidationError("");
  };

  const handleCounterChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    setCounter(value);
    setValidationError("");
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className={`${styles.modal} ${isRTL ? styles.rtl : ""}`}
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          {t("Invoice Numbering Settings")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.modalBody} dir={isRTL ? "rtl" : "ltr"}>
        <div className={styles.settingsContainer}>

          {/* Prefix and Next Number fields - Always visible at top */}
          <div className={styles.inputRow}>
            <div className={styles.inputCol}>
              <label className={styles.label}>
                {t("Prefix")}
              </label>
              <input
                type="text"
                value={prefix}
                onChange={handlePrefixChange}
                className={styles.input}
                placeholder={isRTL ? "فات-" : "INV-"}
                maxLength={10}
                dir="auto"
                disabled={numberingType !== "auto"}
              />
            </div>
            <div className={styles.inputCol}>
              <label className={styles.label}>
                {t("Next Number")}
              </label>
              <input
                type="number"
                value={counter}
                onChange={handleCounterChange}
                className={styles.input}
                min="1"
                dir="ltr"
                disabled={numberingType !== "auto"}
              />
            </div>
          </div>

          {/* Invoice Number Type - Three Options */}
          <div className={styles.settingGroup}>
            <label className={styles.groupLabel}>
              {t("Invoice Number")}
            </label>
            <div className={styles.radioGroup}>
              <Form.Check
                type="radio"
                id="auto-continuous"
                name="numberingType"
                value="auto"
                checked={numberingType === "auto"}
                onChange={handleNumberingTypeChange}
                label={t("Auto-generate continuously")}
                className={styles.radioOption}
              />
              <Form.Check
                type="radio"
                id="manual-once"
                name="numberingType"
                value="manual_once"
                checked={numberingType === "manual_once"}
                onChange={handleNumberingTypeChange}
                label={t("Add manually once for this invoice")}
                className={styles.radioOption}
              />
              <Form.Check
                type="radio"
                id="manual-each-time"
                name="numberingType"
                value="manual_each_time"
                checked={numberingType === "manual_each_time"}
                onChange={handleNumberingTypeChange}
                label={t("Add manually each time")}
                className={styles.radioOption}
              />
            </div>
          </div>

          {/* Include year checkbox - only for auto mode */}
          {numberingType === "auto" && (
            <>
              <div className={styles.settingGroup}>
                <Form.Check
                  type="checkbox"
                  id="include-year"
                  checked={includeYear}
                  onChange={(e) => {
                    setIncludeYear(e.target.checked);
                    setValidationError("");
                  }}
                  label={t("Include year in invoice number")}
                  className={styles.checkbox}
                />
              </div>

              {/* Invoice Number Preview */}
              <div className={styles.previewContainer}>
                <label className={styles.previewLabel}>
                  {t("Invoice Number")}
                </label>
                <div className={styles.previewBox}>
                  <span className={styles.previewText} dir="ltr">{preview}</span>
                </div>
              </div>

              {/* Validation Error */}
              {validationError && (
                <div className={styles.errorMessage}>
                  {validationError}
                </div>
              )}
            </>
          )}

          {/* Manual mode notes */}
          {numberingType === "manual_each_time" && (
            <div className={styles.manualNote}>
              <p>{t("You will enter the invoice number manually for each invoice")}</p>
            </div>
          )}
          {numberingType === "manual_once" && (
            <div className={styles.manualNote}>
              <p>{t("Enter the invoice number manually for this invoice only")}</p>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        <Button
          variant="secondary"
          onClick={onHide}
          className={styles.cancelBtn}
        >
          {t("Cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={validating || ((numberingType === "auto" || numberingType === "auto_yearly") && !prefix)}
          className={styles.saveBtn}
        >
          {validating ? t("Validating...") : t("Save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceNumberingSettings;